import {
    useCallback,
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle
} from "react";
import axios from "axios";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "./Table";
import LoadingBar from "react-top-loading-bar";

const EntityTable = forwardRef(({
    endpoint,
    columns,
    defaultSort = "id",
    defaultDirection = "desc",
    pageSize = 20,
    extraParams = {},
    searchable = true,
    autoFocusSearch = false,
    multiSelect = false,
    selectionActions = [],
    onSelectionChange,
    searchPlaceholder,
    onExport,
    onReset,
    rowId,
    showColumnsButton,
    showExportButton,
    searchMaxWidth,
    hoverable = true,
    emptyMessage,
    onRowDoubleClick,
    onRowClick,
    getRowDragData,
    onTotalChange,
    enableCardView,
    renderCard,
    // mic in the search field (dictation) — e.g. the products listing
    voiceSearch = false,
    // extra class(es) on a row from its data (Table rowClassName), e.g. today's sales
    rowClassName,
    // placeholder rows on first load (Table skeletonRows) — on for every listing, enough to fill
    // the window whatever the row height (short sales rows left half the page empty); 0 = spinner
    skeletonRows = Math.ceil((typeof window !== 'undefined' ? window.innerHeight : 900) / 52),
}, ref) => {

    // Restore the previous search/sort/filters for this listing (e.g. after
    // navigating to a detail page and coming back) — scoped to this session only.
    const location   = useLocation();
    const storageKey = `entityTableState:${location.pathname}`;
    const [persisted] = useState(() => {
        try {
            const raw = sessionStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });
    const persistQuery = () => {
        try { sessionStorage.setItem(storageKey, JSON.stringify(queryRef.current)); } catch {}
    };

    // Mirrors how many pages have been loaded (via infinite scroll) in a `?page=` URL param —
    // 0 is omitted so a fresh listing keeps a clean URL. Read once at mount so a shared/
    // refreshed link restores the same scroll depth instead of always starting at page 0; not
    // re-read afterward since a new search/sort/filter always resets to page 0 regardless of
    // what's currently in the URL (see the query-change effect below).
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPageRef = useRef(Math.max(0, parseInt(searchParams.get('page'), 10) || 0));
    // Tracks the [keyword, sortField, sortDirection] tuple the query-change effect last acted
    // on, so it can tell "this is the very first run" (and StrictMode's dev-only double-invoke
    // of that same first run, which must behave identically) apart from "the user actually
    // changed something" — null means neither has happened yet, and a repeat of the exact same
    // tuple means it's StrictMode replaying the first run, not a real change.
    const lastAppliedQueryKeyRef = useRef(null);

    // Browsers throttle history.pushState/replaceState past a certain call rate (Chrome caps
    // it around 100 calls per 10s and silently drops the rest) — the debounced scroll-fraction
    // handler recomputes on every ~200ms lull while scrolling and very often lands on the same
    // page number it already set, so without this guard a long scroll session burns through
    // that budget on no-op writes and later, genuine page changes stop reaching the URL at all.
    const lastUrlPageRef = useRef(initialPageRef.current);

    const updatePageParam = useCallback((pageNum) => {
        if (lastUrlPageRef.current === pageNum) return;
        lastUrlPageRef.current = pageNum;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (pageNum > 0) next.set('page', String(pageNum));
            else next.delete('page');
            return next;
        }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadingBarRef  = useRef(null);
    const tableInnerRef  = useRef(null);
    const controller     = useRef(null);
    const pageRef        = useRef(0);
    const loadingRef     = useRef(false);
    const hasMoreRef     = useRef(false);
    // all query params in one ref — avoids stale closures
    const queryRef       = useRef({
        keyword: persisted?.keyword ?? "",
        sortField: persisted?.sortField ?? defaultSort,
        sortDirection: persisted?.sortDirection ?? defaultDirection,
        filters: persisted?.filters ?? {},
    });
    // keep extraParams in a ref so it never invalidates fetchPage
    const extraParamsRef = useRef(extraParams);
    useEffect(() => { extraParamsRef.current = extraParams; });

    // same treatment for onTotalChange — an inline callback from the parent
    // must not force fetchPage (and the fetch effect that depends on it) to
    // recreate on every parent render
    const onTotalChangeRef = useRef(onTotalChange);
    useEffect(() => { onTotalChangeRef.current = onTotalChange; });

    const [data, setData]               = useState([]);
    const [keyword, setKeyword]         = useState(persisted?.keyword ?? "");
    const [sortField, setSortField]     = useState(persisted?.sortField ?? defaultSort);
    const [sortDirection, setSortDir]   = useState(persisted?.sortDirection ?? defaultDirection);
    const [filtersState, setFiltersState] = useState(persisted?.filters ?? {});
    const [totalElements, setTotal]     = useState(0);
    const [hasMore, setHasMore]         = useState(false);
    const [loading, setLoading]         = useState(true);

    // Truly stable — reads everything from refs, only needs endpoint + pageSize
    const fetchPage = useCallback(async (pageNum, append) => {
        if (loadingRef.current && append) return;

        controller.current?.abort();
        const myController = new AbortController();
        controller.current = myController;
        loadingRef.current = true;
        setLoading(true);
        if (!append) loadingBarRef.current?.continuousStart();

        try {
            const q = queryRef.current;
            const res = await axios.get(endpoint, {
                withCredentials: true,
                signal: myController.signal,
                params: {
                    page: pageNum,
                    size: pageSize,
                    keyword: q.keyword,
                    sortField: q.sortField,
                    sortDirection: q.sortDirection,
                    ...extraParamsRef.current,
                    ...q.filters,
                },
            });

            const items = res.data.content || [];
            setData(prev => append ? [...prev, ...items] : items);
            const total = res.data.totalElements || 0;
            setTotal(total);
            onTotalChangeRef.current?.(total);

            const more = pageNum < (res.data.totalPages || 1) - 1;
            setHasMore(more);
            hasMoreRef.current = more;

        } catch (err) {
            if (err.name !== "CanceledError") {
                console.error("EntityTable:", err);
                hasMoreRef.current = false;
                setHasMore(false);
            }
        } finally {
            // Only the most recent request clears the loading state.
            // Aborted requests (StrictMode double-invoke, rapid filter changes) must
            // not call setLoading(false) — they'd race with the new request's setLoading(true).
            if (controller.current === myController) {
                loadingRef.current = false;
                setLoading(false);
            }
            loadingBarRef.current?.complete();
        }
    }, [endpoint, pageSize]); // stable — no extraParams / keyword / sort in deps

    // Single effect — ONE fetch on mount, ONE on any query-param change
    useEffect(() => {
        queryRef.current = {
            ...queryRef.current,
            keyword,
            sortField,
            sortDirection,
        };
        persistQuery();
        setData([]);

        const queryKey = JSON.stringify([keyword, sortField, sortDirection]);
        const isFirstOrStrictModeReplay = lastAppliedQueryKeyRef.current === null
            || lastAppliedQueryKeyRef.current === queryKey;
        lastAppliedQueryKeyRef.current = queryKey;

        // Any search/sort/filter change starts back at page 0 — only the very first run (a
        // fresh mount, possibly restoring a shared/refreshed URL) honors ?page=. StrictMode
        // (dev only) runs this effect twice for that same first mount — the second pass must
        // replay identically rather than treat itself as a real change, hence comparing against
        // the previous key instead of a plain "have I run yet" flag.
        if (!isFirstOrStrictModeReplay) {
            pageRef.current = 0;
            updatePageParam(0);
            fetchPage(0, false);
            return;
        }

        const targetPage = initialPageRef.current;
        if (targetPage === 0) {
            pageRef.current = 0;
            fetchPage(0, false);
            return;
        }

        // Restoring a URL that points past page 0 — sequentially reload every page up to it so
        // the accumulated list matches what infinite scroll would have built, then jump the
        // scroll position down to it too — otherwise all the right rows are loaded (the counter
        // reads correctly) but the view stays stuck at the top, showing just page 0's rows.
        let cancelled = false;
        (async () => {
            for (let p = 0; p <= targetPage; p++) {
                if (cancelled) return;
                await fetchPage(p, p > 0);
            }
            if (cancelled) return;
            pageRef.current = targetPage;
            // Deferred a frame so the DOM has actually painted the newly-loaded rows —
            // scrolling immediately after the last setData call would still measure the
            // pre-update scrollHeight. scrollNearBottom (not scrollToBottom) deliberately
            // stops just short of the true bottom — landing exactly at scrollHeight sits
            // inside Table.js's own "near bottom, load more" trigger zone, which fired an
            // unwanted extra page load (bumping the URL to page+1) the instant this ran.
            // (This scroll moves downward, so — same as loadMore below — Table.js's
            // direction-gated scroll-fraction handler won't fire from it at all.)
            requestAnimationFrame(() => {
                if (!cancelled) tableInnerRef.current?.scrollNearBottom();
            });
        })();
        return () => { cancelled = true; };
    }, [keyword, sortField, sortDirection, fetchPage, updatePageParam]);

    const loadMore = useCallback(() => {
        if (loadingRef.current || !hasMoreRef.current) return;
        const next = pageRef.current + 1;
        pageRef.current = next;
        updatePageParam(next);
        fetchPage(next, true);
    }, [fetchPage, updatePageParam]);

    // Scrolling back up should walk ?page= back down too, not just track the highest page ever
    // loaded — nothing gets unloaded on scroll-up (infinite scroll only ever appends), so this
    // maps the scroll position onto whichever page's worth of already-loaded rows it falls
    // within, out of the pages loaded so far (pageRef.current). Table.js only invokes this for
    // upward scrolling in the first place — downward scroll (loading more) never reaches it, so
    // there's no race with loadMore bumping the page the other way.
    const handleScrollFraction = useCallback((fraction) => {
        const highestLoaded = pageRef.current;
        if (highestLoaded <= 0) return;
        const estimated = Math.max(0, Math.min(highestLoaded, Math.round(fraction * highestLoaded)));
        updatePageParam(estimated);
    }, [updatePageParam]);

    const handleFilter = useCallback((f) => {
        queryRef.current.filters = f;
        setFiltersState(f);
        persistQuery();
        pageRef.current = 0;
        updatePageParam(0);
        setData([]);
        fetchPage(0, false);
    }, [fetchPage, updatePageParam]);

    const handleReset = useCallback(() => {
        queryRef.current.filters = {};
        setFiltersState({});
        persistQuery();
        setKeyword("");
        pageRef.current = 0;
        updatePageParam(0);
        setData([]);
        fetchPage(0, false);
        onReset?.();
    }, [fetchPage, onReset, updatePageParam]);

    useImperativeHandle(ref, () => ({
        reload: () => { pageRef.current = 0; updatePageParam(0); setData([]); fetchPage(0, false); },
        resetFilters: () => { queryRef.current.filters = {}; setFiltersState({}); setKeyword(""); persistQuery(); },
        setKeyword: (value) => setKeyword(value),
        setFilters: (f) => handleFilter(f),
        getData: () => data,
    }));

    // Tracks the current table selection so handleExport can read it without stale closures
    const selectionRef = useRef({ ids: [], selectAll: false });

    const handleInternalSelectionChange = useCallback((ids, meta) => {
        selectionRef.current = { ids, selectAll: meta?.selectAll || false };
        onSelectionChange?.(ids, meta);
    }, [onSelectionChange]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    const handleExport = useCallback(async (format) => {
        if (format === 'email') { toast.info('Export par email bientôt disponible'); return; }

        const { ids: selectedIds, selectAll } = selectionRef.current;
        const params = new URLSearchParams();
        params.set('format', format);

        if (selectedIds.length > 0 && !selectAll) {
            // Export only the checked items
            params.set('ids', selectedIds.join(','));
        } else {
            // Export all matching the current search/filters
            const q = queryRef.current;
            if (q.keyword) params.set('keyword', q.keyword);
            params.set('sortField', q.sortField);
            params.set('sortDirection', q.sortDirection);
            Object.entries(extraParamsRef.current).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
            Object.entries(q.filters).forEach(([k, v]) => { if (v != null && v !== '') params.set(k, String(v)); });
        }

        try {
            const response = await fetch(`${endpoint}/export?${params}`, { credentials: 'include' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const cd = response.headers.get('Content-Disposition') || '';
            const filename = cd.match(/filename=(.+)/)?.[1] || `export.${format}`;

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export error:', err);
            toast.error("Échec de l'export");
        }
    }, [endpoint]);

    return (
        <>
            <LoadingBar
                ref={loadingBarRef}
                color="#0d6efd"
                height={3}
                shadow={true}
            />

            <Table
                ref={tableInnerRef}
                columns={columns}
                data={data}
                totalElements={totalElements}
                loading={loading}
                keyword={keyword}
                setKeyword={searchable ? setKeyword : null}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                filters={filtersState}
                onFilter={handleFilter}
                onLoadMore={loadMore}
                hasMore={hasMore}
                onScrollFraction={handleScrollFraction}
                multiSelect={multiSelect}
                selectionActions={selectionActions}
                onSelectionChange={handleInternalSelectionChange}
                searchPlaceholder={searchPlaceholder}
                autoFocusSearch={autoFocusSearch}
                onExport={onExport ?? handleExport}
                onReset={handleReset}
                rowId={rowId}
                {...(showColumnsButton != null ? { showColumnsButton } : {})}
                {...(showExportButton != null ? { showExportButton } : {})}
                {...(searchMaxWidth != null ? { searchMaxWidth } : {})}
                {...(emptyMessage != null ? { emptyMessage } : {})}
                hoverable={hoverable}
                onRowDoubleClick={onRowDoubleClick}
                onRowClick={onRowClick}
                getRowDragData={getRowDragData}
                enableCardView={enableCardView}
                renderCard={renderCard}
                skeletonRows={skeletonRows}
                voiceSearch={voiceSearch}
                rowClassName={rowClassName}
            />
        </>
    );
});

export default EntityTable;